// edit-notification-settings.component.ts
import {Component, Inject} from '@angular/core';
import {DialogBaseComponent} from "../dialog-base/dialog-base.component";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {GameService} from "../../../services/game.service";
import {ActiveUserService} from "../../../services/active-user.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatError, MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {NgIf} from "@angular/common";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {DateService} from "../../../services/date.service";
import {IUpdateNotificationSettings} from "../../../interfaces/IUpdateNotificationSettings";
import {DRAW_REMINDER_TIMES_INVALID_ERROR_TYPE, DRAW_REMINDER_TIMES_INVALID_MESSAGE} from "../../../consts/DrawReminderSettingsErrors";

@Component({
  selector: 'app-edit-notification-settings',
  standalone: true,
  imports: [
    DialogBaseComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    ReactiveFormsModule,
    MatButton,
    NgIf
  ],
  templateUrl: './edit-notification-settings.component.html',
  styleUrl: './edit-notification-settings.component.css'
})
export class EditNotificationSettingsComponent {

  public timesOutOfOrder = false;
  protected readonly timesOutOfOrderMessage = DRAW_REMINDER_TIMES_INVALID_MESSAGE;

  public uiState = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;

  private militaryTime: RegExp = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;

  public notificationsBeginTimeControl = new FormControl<string | null>(null, [Validators.required, Validators.pattern(this.militaryTime)]);
  public notificationsEndTimeControl = new FormControl<string | null>(null, [Validators.required, Validators.pattern(this.militaryTime)]);
  // Frequency and chunk size are deliberately absent: they are the send rate against Causable's shared email
  // provider, and live behind the Causable-admin-only EditNotificationDeliverySettingsComponent.
  public notificationSettingsForm = new FormGroup({
    notificationsBeginTime: this.notificationsBeginTimeControl,
    notificationsEndTime: this.notificationsEndTimeControl
  });

  constructor(private dialogRef: MatDialogRef<EditNotificationSettingsComponent>,
              private gameService: GameService,
              private activeUserService: ActiveUserService,
              private dateService: DateService,
              @Inject(MAT_DIALOG_DATA) public game: IGameQueryResult,
              private snackBarService: SnackbarService) {

    // Calculate actual times for notifications
    const drawTime = this.game.DrawTimeMinutesFromMidnightLocal;

    if (this.game.NotificationsBeginMinutesBeforeDraw !== undefined) {
      const beginTime = this.dateService.convertToMilitaryTime(drawTime - this.game.NotificationsBeginMinutesBeforeDraw);
      this.notificationsBeginTimeControl.setValue(beginTime);
    }

    if (this.game.NotificationsEndMinutesBeforeDraw !== undefined) {
      const endTime = this.dateService.convertToMilitaryTime(drawTime - this.game.NotificationsEndMinutesBeforeDraw);
      this.notificationsEndTimeControl.setValue(endTime);
    }

    this.notificationSettingsForm.valueChanges.subscribe(() => this.timesOutOfOrder = false);
  }

  submit() {
    if (this.notificationSettingsForm.invalid) {
      this.notificationSettingsForm.markAllAsTouched();
      return;
    }

    const drawTime = this.game.DrawTimeMinutesFromMidnightLocal;
    const beginTime = this.dateService.convertMilitaryTimeToMinutes(this.notificationsBeginTimeControl.value!);
    const endTime = this.dateService.convertMilitaryTimeToMinutes(this.notificationsEndTimeControl.value!);

    // Matches DrawReminderSettings.TimesAreValid: the hourly job needs an hour-long window to be sure of a run.
    this.timesOutOfOrder = !(beginTime + 60 <= endTime && endTime <= drawTime);
    if (this.timesOutOfOrder) {
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    let command: IUpdateNotificationSettings = {
      AdminId: this.activeUserService.activeUser().Id,
      GameId: this.game.Id,
      NotificationsBeginMinutesBeforeDraw: drawTime - beginTime,
      NotificationsEndMinutesBeforeDraw: drawTime - endTime
    };

    this.gameService.updateGameNotificationSettings(command).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackBarService.openSuccessfulSnackBar("Successfully updated notification settings!");
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        if (err?.Error?.ErrorType === DRAW_REMINDER_TIMES_INVALID_ERROR_TYPE) {
          this.timesOutOfOrder = true;
          return;
        }
        this.snackBarService.openErrorSnackBar("Failed to update notification settings!");
      }
    });
  }
}
